import { easeInOut, motion } from 'motion/react'
import { Shield } from 'lucide-react'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

export default function Privacy() {
  return (
    <PageLayout>
      <Seo
        title="Privacy Policy | SOTA Navigator"
        description="How SOTA Navigator collects, stores and processes your data, the legal basis for each purpose, and the rights you can exercise over it."
      />

      <div className="relative overflow-hidden bg-[#001871] text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2 }}
          className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
        />

        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1 }}
          className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />

        <div className="relative pb-40 pt-32 sm:pb-48 sm:pt-40">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="space-y-8"
              >
                <motion.div
                  variants={glowAnimation}
                  initial="initial"
                  animate="animate"
                  className="inline-flex items-center gap-2 rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                >
                  <Shield className="size-4" />
                  Your Data Privacy Matters
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                  className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                >
                  Privacy Policy
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
                >
                  How we protect and handle your data with the utmost care and transparency
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden leading-none">
          <svg
            className="relative block h-[50px] w-full"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </div>

      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-[#001871]">Introduction</h2>
                <p className="text-lg text-gray-700">
                  At SOTA Navigator, we believe in complete transparency about how we handle your
                  data. Our Privacy Policy is designed to help you understand what information we
                  collect, why we collect it, and how we protect it. We're committed to safeguarding
                  your privacy while providing you with the best possible research experience.
                </p>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#001871]">Information We Collect</h3>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
                      <h4 className="mb-4 font-semibold text-[#001871]">Account Information</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Full name and title</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Email address</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Institution affiliation</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Professional role</span>
                        </li>
                      </ul>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
                      <h4 className="mb-4 font-semibold text-[#001871]">Research Data</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Uploaded documents</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Search queries</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Analysis preferences</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Research metadata</span>
                        </li>
                      </ul>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
                      <h4 className="mb-4 font-semibold text-[#001871]">Usage Data</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Feature preferences</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Interface settings</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Time zone settings</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Session analytics</span>
                        </li>
                      </ul>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
                      <h4 className="mb-4 font-semibold text-[#001871]">Communication</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Support requests</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Feedback submissions</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Newsletter preferences</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-[#001871]">•</span>
                          <span>Communication history</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-gray-50 p-8">
                  <h3 className="text-xl font-semibold text-[#001871]">
                    How We Use Your Information
                  </h3>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0">
                          <div className="flex size-8 items-center justify-center rounded-lg bg-[#001871] text-white">
                            1
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Service Provision</h4>
                          <p className="text-sm text-gray-600">
                            Maintain and improve our platform functionality and user experience
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="shrink-0">
                          <div className="flex size-8 items-center justify-center rounded-lg bg-[#001871] text-white">
                            2
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Personalization</h4>
                          <p className="text-sm text-gray-600">
                            Customize and enhance your research workflow experience
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0">
                          <div className="flex size-8 items-center justify-center rounded-lg bg-[#001871] text-white">
                            3
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Communication</h4>
                          <p className="text-sm text-gray-600">
                            Send important updates, features announcements, and support responses
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="shrink-0">
                          <div className="flex size-8 items-center justify-center rounded-lg bg-[#001871] text-white">
                            4
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Security</h4>
                          <p className="text-sm text-gray-600">
                            Protect your data and maintain platform security
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#001871]">Your Privacy Rights</h3>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[
                      {
                        title: 'Access Your Data',
                        description: 'Request a copy of your personal data',
                      },
                      {
                        title: 'Data Correction',
                        description: 'Update or correct your personal information',
                      },
                      {
                        title: 'Data Deletion',
                        description: 'Request removal of your personal data',
                      },
                      {
                        title: 'Processing Objection',
                        description: 'Object to how we process your data',
                      },
                      {
                        title: 'Data Portability',
                        description: 'Receive your data in a structured format',
                      },
                      {
                        title: 'Consent Withdrawal',
                        description: 'Withdraw previously given consent',
                      },
                    ].map((right) => (
                      <div
                        key={right.title}
                        className="rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <h4 className="font-medium text-[#001871]">{right.title}</h4>
                        <p className="mt-1 text-sm text-gray-600">{right.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
